<template>
  <FormDialog
    v-model="internalModel"
    title="Edit Profile Information"
    :loading="loading"
    :submit-disabled="!isFormValid"
    :show-close-button="!needsTermsAcceptance"
    submit-label="Save Profile"
    @submit="handleSubmit"
  >
    <v-form ref="formRef" v-model="isFormValid">
      <p class="text-body-2 text-medium-emphasis mb-4">
        Update your profile information.
      </p>

      <FormTextField
        v-model="formData.givenName"
        label="First Name"
        required
        variant="outlined"
        density="comfortable"
        class="mb-2"
        :disabled="loading"
      />

      <FormTextField
        v-model="formData.surname"
        label="Last Name"
        required
        variant="outlined"
        density="comfortable"
        class="mb-2"
        :disabled="loading"
      />

      <FormTextField
        v-model="formData.jobTitle"
        label="Job Title"
        required
        variant="outlined"
        density="comfortable"
        :disabled="loading"
      />

      <v-checkbox
        v-if="needsTermsAcceptance"
        v-model="formData.acceptTerms"
        class="mt-0"
        color="primary"
        :rules="termsRules"
        :disabled="loading"
      >
        <template #label>
          I agree to the
          <v-dialog v-model="termsDialog" scrollable width="620px" aria-label="Terms of Use">
            <template #activator="{ props }">
              <a
                class="px-1"
                style="font-weight: 500; text-decoration: underline; color: rgb(var(--v-theme-primary));"
                v-bind="props"
                @click.stop.prevent=""
                @mousedown.stop
                @touchstart.stop
              >
                Terms of Use
              </a>
            </template>
            <v-card>
              <v-card-title style="font-weight: 500; font-size: 22px">Terms of Use</v-card-title>
              <v-divider />
              <v-card-text style="height: 500px">
                <p>
                  The following outlines the Terms of Use ("The Terms") that are to be adhered to by all users
                  of the Emergency Accommodation Management System ("The System"), also known as EAMS,
                  including the product owner (McAuley Community Services for Women – "The Owner"),
                  and the product developers (DocuLocker, ABN: 31 678 878 487, and Unicorn Studio Co,
                  ABN: 19 361 522 426 – collectively "The Developers").
                </p>
                <br />
                <p>The term "System Users" includes the following:</p>
                <ul class="term-lists">
                  <li>Standard users ("Standard Users")</li>
                  <li>Managerial administrators ("Managerial Admin")</li>
                  <li>Super administrators ("Super-Admin")</li>
                </ul>
                <br />
                <h3>1. PRIVACY POLICY</h3>
                <p>
                  The Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth)
                  (the Privacy Act) governs the way in which personal and Confidential Information must be
                  collected, used, disclosed, stored, secured and disposed of. The Developers have ensured that
                  personal and confidential information featured on The System has been stored in a manner
                  that reasonably protects it from misuse and loss, from unauthorized access, modification,
                  and/or disclosure, and is in line with the principles outlined in the Privacy Act.
                  Personal information may be disclosed by The Developers or The Owner where required or
                  authorised by law.
                </p>
                <br />
                <p>
                  A copy of the Australian Privacy Principles may be obtained from the website of
                  The Office of the Australian Information Commissioner at
                  <a href="https://www.oaic.gov.au/" target="_blank">https://www.oaic.gov.au/</a>.
                </p>
                <br />
                <h3>2. CONFIDENTIALITY</h3>
                <p>
                  "Confidential Information" means all information that is expressly designated as confidential,
                  and is inclusive of employee information, proprietary information, and information regarding
                  any third-party company external to the authorised organisations.
                </p>
                <br />
                <p>All information contained in The System is confidential by default, with the following exceptions:</p>
                <ul class="term-lists">
                  <li>Information that is generally known in the industry</li>
                  <li>Information that is currently publicly available through other sources, such as a public website</li>
                  <li>Information that subsequently becomes available to the public through no wrongful act of the System Users</li>
                  <li>Information that is explicitly permitted to be shared externally by authorised organisations, who will determine the terms of the disclosure</li>
                </ul>
                <br />
                <p>
                  Except as otherwise permitted in The Terms, all System Users must not disclose Confidential
                  Information to any persons external to the authorised organisations. When sharing Confidential
                  Information internally, System Users must adhere to the organisational policies of each
                  institution relevant to the person making the disclosure, and the person receiving disclosed
                  Confidential Information.
                </p>
                <br />
                <h3>3. LIABILITY</h3>
                <p>
                  As outlined in Section 1 ("Privacy Policy"), The Developers have made all efforts to provide
                  a secure system that does not leave confidential information vulnerable to attack or compromise.
                  However, in the event that such an attack or compromise does occur, The Developers will not
                  be liable for any subsequent damages arising from such an event. To the extent permitted by law,
                  in no event shall The Developers be liable for any indirect, punitive, or consequential
                  damages, including lost profits or business opportunities.
                </p>
                <br />
                <p>
                  Whilst The Owner has made all attempts to ensure accuracy of the information featured on
                  The System, The Owner and The Developers will take no responsibility and
                  hold no liability for losses caused by reliance on the information supplied.
                </p>
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn color="primary" variant="text" @click="termsDialog = false">OK</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-checkbox>
    </v-form>
  </FormDialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '../stores/authStore'
import { useNotificationStore } from '../stores/notificationStore'
import FormDialog from './shared/FormDialog.vue'
import FormTextField from './shared/FormTextField.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const authStore = useAuthStore()
const notificationStore = useNotificationStore()

const formRef = ref(null)
const isFormValid = ref(false)
const loading = ref(false)
const termsDialog = ref(false)

const formData = ref({
  givenName: '',
  surname: '',
  jobTitle: '',
  acceptTerms: false
})

const termsRules = [
  v => !!v || 'You must agree to the Terms of Use to continue'
]

const needsTermsAcceptance = computed(() => {
  return !authStore.userProfile?.termsAcceptedAt
})

const internalModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Watch for profile changes and populate form
watch(() => authStore.userProfile, (profile) => {
  if (profile) {
    formData.value = {
      givenName: profile.givenName || '',
      surname: profile.surname || '',
      jobTitle: profile.jobTitle || '',
      acceptTerms: false
    }
  }
}, { immediate: true })

const handleSubmit = async () => {
  // Validate form
  const { valid } = await formRef.value.validate()
  if (!valid) {
    notificationStore.showError('Please fill in all required fields')
    return
  }

  loading.value = true
  try {
    // Combine first name and last name to create display name
    const givenName = formData.value.givenName.trim()
    const surname = formData.value.surname.trim()

    // Build display name from non-empty name parts
    const nameParts = []
    if (givenName) nameParts.push(givenName)
    if (surname) nameParts.push(surname)
    const displayName = nameParts.join(' ')

    const payload = {
      givenName,
      surname,
      displayName,
      jobTitle: formData.value.jobTitle.trim()
    }

    // Include acceptTerms if user needs to accept terms
    if (needsTermsAcceptance.value && formData.value.acceptTerms) {
      payload.acceptTerms = true
    }

    await authStore.updateUserProfile(payload)

    notificationStore.showSuccess('Profile updated successfully')
    emit('saved')
    internalModel.value = false
  } catch (error) {
    notificationStore.showError(error.message || 'Failed to update profile')
  } finally {
    loading.value = false
  }
}

</script>

<style scoped>
.term-lists {
  padding-left: 1.5em;
  list-style-type: disc;
}
</style>
